// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module provides a map component for visualizing multiple locations.
 * It wraps the base Map component with location-specific markers and interactions.
 */
import { Map } from '@/components/map';
import { LocationFilter } from '@/location/api/get-locations';
import { Location } from '@/location/api/types';
import { LocationMapMarker } from '@/location/components/map-marker';
import { useLocations } from '@/location/hooks/use-locations';
import { SparseLocation } from '@/sparse/api/types';

interface StaticLocationProps {
  /**
   * Array of locations to display on the map.
   * Supports both full Location and SparseLocation objects.
   * Each location will be represented by a marker showing its asset count (if available).
   */
  locations: (Location | SparseLocation)[];

  /**
   * Optional center point for the map view.
   * If provided, the map will be centered on these coordinates.
   * If null or undefined, the map will automatically center to show all markers.
   */
  center?: { latitude: number; longitude: number } | null;

  /**
   * Optional callback fired when a location marker is clicked.
   * Receives the location's key as a parameter.
   * Typically used to open detailed views or navigate to location-specific pages.
   *
   * @param key - The unique key identifier of the clicked location
   */
  onClickLocation?: (key: string) => void;
}

export function StaticLocationMap(props: StaticLocationProps) {
  const createClickHandler = (location: Location | SparseLocation) => () => {
    if (props.onClickLocation) {
      props.onClickLocation(location.key);
    }
  };

  return (
    <Map center={props.center}>
      {props.locations.map((location) => (
        <LocationMapMarker
          key={location.id}
          location={location}
          onClick={createClickHandler(location)}
        />
      ))}
    </Map>
  );
}

/**
 * Props for the LocationMap component.
 */
interface LocationMapProps {
  /**
   * Show empty locations
   */
  showEmptyLocations: boolean;
  /**
   * The filter parameters to pass for the hook
   */
  filter: LocationFilter;
  /**
   * Optional center point for the map view.
   * If provided, the map will be centered on these coordinates.
   * If null or undefined, the map will automatically center to show all markers.
   */
  center?: { latitude: number; longitude: number } | null;

  /**
   * Optional callback fired when a location marker is clicked.
   * Receives the location's key as a parameter.
   * Typically used to open detailed views or navigate to location-specific pages.
   *
   * @param key - The unique key identifier of the clicked location
   */
  onClickLocation?: (key: string) => void;
}

/**
 * A map component that displays multiple locations with interactive markers.
 *
 * Features:
 * - Displays multiple location markers simultaneously
 * - Supports both full and sparse location data
 * - Shows asset counts in markers when available
 * - Handles click interactions for individual locations
 * - Supports custom map centering
 * - Automatically adjusts to show all markers when no center is specified
 *
 * @example
 * ```tsx
 * // Basic usage with automatic centering
 * <LocationMap
 *   locations={locationsList}
 *   onClickLocation={(key) => openLocationDetails(key)}
 * />
 *
 * // With specific center point
 * <LocationMap
 *   locations={locationsList}
 *   center={{ latitude: 37.7749, longitude: -122.4194 }}
 *   onClickLocation={handleLocationClick}
 * />
 * ```
 */
export function LocationMap(props: LocationMapProps) {
  const { locations } = useLocations({
    filter: props.filter,
  });
  const filteredLocations = props.showEmptyLocations
    ? locations
    : locations.filter((location) => location.assetCount > 0);
  return (
    <StaticLocationMap
      locations={filteredLocations}
      center={props.center}
      onClickLocation={props.onClickLocation}
    />
  );
}
